// Centralized reusable type definitions for the Printers module
// This file contains only reusable data types and interfaces, not component-specific props

// ============================================================================
// Core Data Interfaces
// ============================================================================

/**
 * Calibration message types
 */
export type CaliMessage =
    | 'Calibrated'
    | 'Cancelled'
    | 'PrelinError'
    | 'InError'
    | 'PrintError'
    | 'Not_Calibrated'
    | 'Unknown'
    | 'PreCalibrating'
    | 'Calibrating'
    | 'PreChecking'
    | 'Maintenance'
    | 'Paused';

/**
 * Known calibration set message details
 */
export const knownCaliSetMessageDetails = [
    'CaliPrecheckProcessFinishedSuccessfully',
    'CaliProcessFinishedSuccessfully',
    'AutoCalibrationCanceledByUser',
    'MaxIterationsReached',
    'PreLinFinishedUnsuccessfully',
] as const;

export type KnownCaliSetMessageDetails = (typeof knownCaliSetMessageDetails)[number];

/**
 * Calibration state status
 */
export type CaliSetCalibrationStateStatus = 'Succeeded' | 'Failed' | 'Error' | 'Running';

export const CALIBRATION_CANCELED_MESSAGE = 'Cancelled';
export const CALIBRATION_IS_RUNNING_PRINTER_STATUS = 'Calibrating';
export const PRINTER_ERROR_STATUS = 'Error';

/**
 * Calibration state
 */
export type CaliSetCalibrationState = {
    Status: CaliSetCalibrationStateStatus;
    Iteration: number;
    Message: CaliMessage;
    MessageDetail?: KnownCaliSetMessageDetails | string;
    // We inject the timestamp if if is not there for backwards compatibility with older ColorProof versions.
    TimeStamp: string;
};

/**
 * Calibration set interface
 */
export interface CaliSet {
    Name: string;
    Calibrated: boolean;
    CaliSetDetail: string;
    // If State is not null, IsAutoCaliWizardOpen should be true. It means, the AutoCaliWizard is running for this calibration set.
    IsAutoCaliWizardOpen: boolean | null;
    State: CaliSetCalibrationState;
}

/**
 * Base printer interface - core printer information
 */
export interface Ink {
    InkName: string;
    InkPercentage: string;
}

/**
 * Media interfaces
 */
export interface MediaSize {
    id: string;
    Name: string;
}
export interface Media {
    id: string;
    Name: string;
    Sizes: MediaSize[];
}

// ============================================================================
// Action Input Interfaces
// ============================================================================

/**
 * Action parameter interface for printer actions
 */
export interface ActionParameter {
    name: string;
    value: string;
}

/**
 * Printer action types for controlling printer operations
 */
export type PrinterActionType =
    | 'StartPrinter'
    | 'PausePrinter'
    | 'StartCalibration'
    | 'CancelCalibration'
    | 'CalibratePrinter'
    | 'ChangePrinterPaper';


/*
 * Add action input interface for Redux actions
 */

export interface AddActionInput {
    action: PrinterActionType;
    actionParams: ActionParameter[];
    cpId: string;
}

/**
 * Action type for printer actions
 */
export type RemoteActionType = 'StartPrinter' | 'PausePrinter' | 'StartCalibration' | 'CancelCalibration';